import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { NAV_ITEMS, PRACTICE_INFO } from '../../data/navigation';
import s from './Navbar.module.css';

// ─── Submenu flyout (right-side panel off a dropdown item) ───────────────────
function DropdownItem({ item }) {
  const [subOpen, setSubOpen] = useState(false);
  const closeTimer = useRef(null);

  const openSub = useCallback(() => {
    clearTimeout(closeTimer.current);
    setSubOpen(true);
  }, []);

  const scheduleSub = useCallback(() => {
    closeTimer.current = setTimeout(() => setSubOpen(false), 120);
  }, []);

  // Clean up timer on unmount
  useEffect(() => () => clearTimeout(closeTimer.current), []);

  if (!item.hasSubmenu || !item.submenu) {
    return item.external ? (
      <a href={item.path} target="_blank" rel="noopener noreferrer" className={s.dropLink}>
        {item.icon && <span className={s.dropIcon}>{item.icon}</span>}
        <span>{item.label}</span>
        <span className={s.externalIcon}>↗</span>
      </a>
    ) : (
      <Link to={item.path} className={s.dropLink}>
        {item.icon && <span className={s.dropIcon}>{item.icon}</span>}
        <span>{item.label}</span>
      </Link>
    );
  }

  return (
    <div
      className={s.dropItem}
      onMouseEnter={openSub}
      onMouseLeave={scheduleSub}
    >
      <Link to={item.path} className={`${s.dropLink} ${s.dropLinkHasChild}`}>
        {item.icon && <span className={s.dropIcon}>{item.icon}</span>}
        <span>{item.label}</span>
        <svg className={s.dropArrow} width="12" height="12" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2">
          <path d="m9 18 6-6-6-6"/>
        </svg>
      </Link>

      {subOpen && (
        <div
          className={s.submenu}
          onMouseEnter={openSub}   // keep open when mouse enters submenu
          onMouseLeave={scheduleSub}
        >
          {item.submenu.map(sub => (
            <Link key={sub.path} to={sub.path} className={s.subLink}>
              <span className={s.subBullet}></span>
              {sub.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Top-level nav item with dropdown ────────────────────────────────────────
function NavItem({ item }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();
  const isActive = location.pathname === item.path ||
    location.pathname.startsWith(item.path + '/');

  const openMenu = useCallback(() => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  }, []);

  const scheduleClose = useCallback(() => {
    // 150ms grace period — enough time to move mouse into the dropdown
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  }, []);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  if (!item.children) {
    return (
      <Link to={item.path} className={`${s.navLink} ${isActive ? s.active : ''}`}>
        {item.label}
      </Link>
    );
  }

  return (
    <div
      className={s.navItem}
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      {/* Link navigates to the top-level page; dropdown still opens on hover */}
      <Link to={item.path} className={`${s.navLink} ${isActive ? s.active : ''}`}>
        {item.label}
        <svg
          className={`${s.chevron} ${open ? s.chevronOpen : ''}`}
          width="14" height="14" viewBox="0 0 24 24"
          fill="none" stroke="currentColor" strokeWidth="2"
        >
          <path d="m6 9 6 6 6-6"/>
        </svg>
      </Link>

      {open && (
        <div
          className={s.dropdown}
          onMouseEnter={openMenu}
          onMouseLeave={scheduleClose}
        >
          {item.children.map(child => (
            <DropdownItem key={child.path} item={child} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Navbar ─────────────────────────────────────────────────────────────
export default function Navbar({ scrolled = false }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState({});
  const location = useLocation();

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setMobileExpanded({});
  }, [location.pathname]);

  const toggleMobileSection = (label) => {
    setMobileExpanded(prev => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <nav className={`${s.nav} ${scrolled ? s.scrolled : ''}`}>
      <div className="container">
        <div className={s.inner}>

          {/* Logo
          <Link to="/" className={s.logo}>
            <div className={s.logoMark}>C</div>
            <div className={s.logoText}>
              <span className={s.logoName}>Celebrity Smile Studio</span>
              <span className={s.logoSub}>{PRACTICE_INFO.city}, {PRACTICE_INFO.state}</span>
            </div>
          </Link> */}
          <a href="/"><img src="/images/logo.png" className={s.logo}></img></a>
          {/* Desktop Menu */}
          <div className={s.menu}>
            {NAV_ITEMS.map(item => (
              <NavItem key={item.path} item={item} />
            ))}
          </div>

          {/* Book CTA */}
          <Link to="/book-appointment" className={s.bookBtn}>
            <span className={s.bookPulse}></span>
            Book Appointment
          </Link>

          {/* Mobile Toggle */}
          <button
            className={`${s.mobileToggle} ${mobileOpen ? s.mobileOpen : ''}`}
            onClick={() => setMobileOpen(v => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            <span/><span/><span/>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className={s.mobileMenu}>
          {NAV_ITEMS.map(item => (
            <div key={item.path} className={s.mobileItem}>
              {item.children ? (
                <>
                  <div className={s.mobileLinkRow}>
                    <Link
                      to={item.path}
                      className={s.mobileLink}
                      onClick={() => setMobileOpen(false)}
                    >
                      {item.label}
                    </Link>
                    <button
                      className={s.mobileChevronBtn}
                      onClick={() => toggleMobileSection(item.label)}
                      aria-label={`Toggle ${item.label} menu`}
                    >
                      <svg
                        className={`${s.chevron} ${mobileExpanded[item.label] ? s.chevronOpen : ''}`}
                        width="14" height="14" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" strokeWidth="2"
                      >
                        <path d="m6 9 6 6 6-6"/>
                      </svg>
                    </button>
                  </div>

                  {mobileExpanded[item.label] && (
                    <div className={s.mobileSub}>
                      {item.children.map(child => (
                        <React.Fragment key={child.path}>
                          {/* Parent link */}
                          {child.external ? (
                            <a
                              href={child.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`${s.mobileSubLink} ${s.mobileSubParent}`}
                              onClick={() => setMobileOpen(false)}
                            >
                              {child.icon && <span>{child.icon}</span>}
                              <span>{child.label}</span>
                              <span className={s.externalIcon}>↗</span>
                            </a>
                          ) : (
                            <Link
                              to={child.path}
                              className={`${s.mobileSubLink} ${s.mobileSubParent}`}
                              onClick={() => setMobileOpen(false)}
                            >
                              {child.icon && <span>{child.icon}</span>}
                              <span>{child.label}</span>
                            </Link>
                          )}

                          {/* Nested submenu items */}
                          {child.hasSubmenu && child.submenu && (
                            <div className={s.mobileSubNested}>
                              {child.submenu.map(sub => (
                                <Link
                                  key={sub.path}
                                  to={sub.path}
                                  className={s.mobileSubLink}
                                  onClick={() => setMobileOpen(false)}
                                >
                                  <span className={s.subBullet}></span>
                                  {sub.label}
                                </Link>
                              ))}
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                  className={s.mobileLink}
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}

          <Link
            to="/book-appointment"
            className={s.mobileBook}
            onClick={() => setMobileOpen(false)}
          >
            Book Appointment →
          </Link>
        </div>
      )}
    </nav>
  );
}
